import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, CheckCircle2, PlayCircle, Users, Zap } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import ApexLoader from "@/components/ApexLoader";

const TEAM_SIZES = ["1–5", "6–25", "26–100", "100+"];
const PREFERRED_TIMES = [
  "This week",
  "Next week",
  "Within 2 weeks",
  "Flexible",
];

export default function RequestDemo() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    role: "",
    email: "",
    phone: "",
    team_size: "",
    preferred_time: "",
    message: "",
  });
  const [state, setState] = useState({ loading: false, ok: false, error: "" });

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.company.trim()) {
      setState({ loading: false, ok: false, error: "Name, email, and company are required." });
      return;
    }
    setState({ loading: true, ok: false, error: "" });
    try {
      await api.post("/request-demo", form);
      setState({ loading: false, ok: true, error: "" });
      setForm({
        name: "",
        company: "",
        role: "",
        email: "",
        phone: "",
        team_size: "",
        preferred_time: "",
        message: "",
      });
    } catch (err) {
      setState({ loading: false, ok: false, error: formatApiError(err) });
    }
  };

  return (
    <section
      id="request-demo"
      className="relative py-24 md:py-32 border-t border-white/5 bg-[#0a0a0b]"
      data-testid="request-demo-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="block w-8 h-px bg-[#dc2626]" />
          <span className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-400">
            live walkthrough · direct from the founders
          </span>
        </div>

        <div className="grid md:grid-cols-5 gap-10 md:gap-14 items-start">
          {/* Left: pitch */}
          <div className="md:col-span-2">
            <h2 className="font-display text-5xl md:text-6xl text-white tracking-tight leading-[0.95] mb-8">
              Request a <span className="silver-text italic">demo.</span>
            </h2>
            <p className="font-body text-lg text-zinc-300 leading-relaxed mb-10">
              Thirty minutes, live, with April and Ben. We'll walk your team through
              storm intel, dispatch, AI-drafted estimates, and the founding-network economics —
              tailored to your restoration ops.
            </p>

            <ul className="space-y-4 mb-10">
              {[
                { Icon: PlayCircle, t: "Full platform walkthrough — not a canned deck." },
                { Icon: Zap, t: "Live AI Sentinel + lead-router against your service area." },
                { Icon: Users, t: "Dedicated Q&A with the founders, not a BDR." },
                { Icon: Calendar, t: "Calendar hold lands in your inbox within one business day." },
              ].map(({ Icon, t }) => (
                <li key={t} className="flex items-start gap-3">
                  <Icon size={18} className="text-[#dc2626] mt-0.5 shrink-0" strokeWidth={1.5} />
                  <span className="font-body text-base text-zinc-200 leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>

            <div className="p-4 border border-white/5 bg-[#121214] font-mono-apex text-[10px] uppercase tracking-[0.2em] text-zinc-400">
              Briefings go directly to
              <div className="mt-1 text-zinc-200 tracking-[0.15em]">april@apexinfinityventures.com</div>
              <div className="text-zinc-200 tracking-[0.15em]">ben@apexinfinityventures.com</div>
            </div>
          </div>

          {/* Right: form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="md:col-span-3 bg-[#121214] border border-white/10 p-8 md:p-10"
            data-testid="demo-form-card"
          >
            {state.ok ? (
              <div className="py-8 text-center" data-testid="demo-success">
                <CheckCircle2 size={40} className="text-emerald-400 mx-auto mb-5" strokeWidth={1.5} />
                <div className="font-display text-3xl text-white tracking-tight mb-3">
                  Demo request sent.
                </div>
                <p className="font-body text-base text-zinc-300 max-w-lg mx-auto mb-8">
                  April and Ben have been notified. You'll get a calendar hold within one
                  business day — check your inbox (and spam, just in case).
                </p>
                <button
                  onClick={() => setState({ loading: false, ok: false, error: "" })}
                  className="inline-flex items-center gap-2 btn-secondary-apex px-6 py-3 font-mono-apex text-[10px] uppercase tracking-[0.25em]"
                  data-testid="demo-send-another"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="demo-form">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">
                      Name *
                    </label>
                    <input
                      value={form.name}
                      onChange={onChange("name")}
                      className="input-apex w-full px-4 py-3 font-body text-sm"
                      placeholder="Full name"
                      data-testid="demo-name-input"
                    />
                  </div>
                  <div>
                    <label className="block font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">
                      Company *
                    </label>
                    <input
                      value={form.company}
                      onChange={onChange("company")}
                      className="input-apex w-full px-4 py-3 font-body text-sm"
                      placeholder="Restoration company"
                      data-testid="demo-company-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">
                      Work email *
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={onChange("email")}
                      className="input-apex w-full px-4 py-3 font-body text-sm"
                      placeholder="you@company.com"
                      data-testid="demo-email-input"
                    />
                  </div>
                  <div>
                    <label className="block font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={onChange("phone")}
                      className="input-apex w-full px-4 py-3 font-body text-sm"
                      placeholder="(optional)"
                      data-testid="demo-phone-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">
                      Role
                    </label>
                    <input
                      value={form.role}
                      onChange={onChange("role")}
                      className="input-apex w-full px-4 py-3 font-body text-sm"
                      placeholder="Owner, Ops lead, Dispatcher…"
                      data-testid="demo-role-input"
                    />
                  </div>
                  <div>
                    <label className="block font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">
                      Team size
                    </label>
                    <div className="flex gap-1.5 flex-wrap">
                      {TEAM_SIZES.map((s) => {
                        const active = form.team_size === s;
                        return (
                          <button
                            type="button"
                            key={s}
                            onClick={() => setForm((p) => ({ ...p, team_size: active ? "" : s }))}
                            className={`px-3 py-2 font-mono-apex text-[10px] uppercase tracking-[0.15em] border transition-colors ${
                              active
                                ? "bg-[#dc2626] border-[#dc2626] text-white"
                                : "bg-transparent border-white/15 text-zinc-300 hover:border-white/40"
                            }`}
                            data-testid={`demo-team-size-${s.replace(/[^a-z0-9]/gi, "")}`}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">
                    Preferred timing
                  </label>
                  <div className="flex gap-1.5 flex-wrap">
                    {PREFERRED_TIMES.map((t) => {
                      const active = form.preferred_time === t;
                      return (
                        <button
                          type="button"
                          key={t}
                          onClick={() => setForm((p) => ({ ...p, preferred_time: active ? "" : t }))}
                          className={`px-3 py-2 font-mono-apex text-[10px] uppercase tracking-[0.15em] border transition-colors ${
                            active
                              ? "bg-[#dc2626] border-[#dc2626] text-white"
                              : "bg-transparent border-white/15 text-zinc-300 hover:border-white/40"
                          }`}
                          data-testid={`demo-time-${t.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">
                    What do you want to see?
                  </label>
                  <textarea
                    value={form.message}
                    onChange={onChange("message")}
                    rows={4}
                    className="input-apex w-full px-4 py-3 font-body text-sm resize-none"
                    placeholder="Dispatch board, AI-drafted estimates, storm tracking for my zip codes…"
                    data-testid="demo-message-input"
                  />
                </div>

                {state.error && (
                  <div
                    className="font-mono-apex text-xs text-[#dc2626]"
                    data-testid="demo-error"
                  >
                    {state.error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={state.loading}
                  className="inline-flex items-center gap-2 btn-primary-apex px-7 py-4 font-mono-apex text-[11px] uppercase tracking-[0.25em] disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="demo-submit-btn"
                >
                  {state.loading ? (
                    <>
                      <ApexLoader inline size={14} /> Sending…
                    </>
                  ) : (
                    <>
                      Request demo <ArrowRight size={14} />
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
